import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, Sparkles, Image, Crown, User, RefreshCw, Download, Star, Trash2, ChevronLeft, AlertTriangle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { BottomNav } from '../components/BottomNav';
import { useAuth } from '../contexts/AuthContext';

const STYLES = [
  { id: 'white_background', label: 'White Background', icon: Image, desc: 'Clean marketplace-ready', color: 'bg-gray-100 text-gray-700', best: 'Amazon, Flipkart, Shopify' },
  { id: 'studio', label: 'Studio', icon: Camera, desc: 'Premium studio photography', color: 'bg-blue-50 text-blue-700', best: 'Website, Catalogues' },
  { id: 'lifestyle', label: 'Lifestyle', icon: Sparkles, desc: 'Natural environment', color: 'bg-green-50 text-green-700', best: 'Social media, Campaigns' },
  { id: 'luxury', label: 'Luxury', icon: Crown, desc: 'Editorial high-end', color: 'bg-purple-50 text-purple-700', best: 'Premium brands, Ads' },
  { id: 'model_photoshoot', label: 'Model Photoshoot', icon: User, desc: 'AI fashion model', color: 'bg-pink-50 text-pink-700', best: 'Fashion, Apparel' },
];

const POSES = ['Standing', 'Walking', 'Sitting', 'Front View', 'Side View', 'Full Length', 'Close-up', 'Editorial'];
const ENVIRONMENTS = ['Studio', 'Luxury Interior', 'Indian Palace', 'Wedding', 'Festive', 'Outdoor', 'Boutique', 'Minimal'];
const LIGHTINGS = ['Natural', 'Studio', 'Soft', 'Cinematic', 'Editorial', 'Golden Hour'];

export const VisualCommerceScreen = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [assets, setAssets] = useState([]);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [instructions, setInstructions] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [modelConfig, setModelConfig] = useState({ gender: 'Woman', pose: 'Standing', environment: 'Studio', lighting: 'Studio', composition: 'Full Body' });

  useEffect(() => {
    fetchProduct();
    fetchAssets();
  }, [productId]);

  const fetchProduct = async () => {
    try {
      const res = await api.get(`/products/${productId}`);
      setProduct(res.data.product);
    } catch { navigate('/my-products'); }
  };

  const fetchAssets = async () => {
    try {
      const res = await api.get(`/products/${productId}/visual-assets`);
      setAssets(res.data.assets || []);
    } catch {}
  };

  const handleGenerate = async () => {
    if (!selectedStyle) { toast.error('Select a photography style'); return; }
    setGenerating(true);
    try {
      const body = {
        style: selectedStyle,
        merchant_instructions: instructions || undefined,
        model_config_data: selectedStyle === 'model_photoshoot' ? modelConfig : undefined,
      };
      const res = await api.post(`/products/${productId}/visuals/generate`, body, { timeout: 120000 });
      if (res.data.duplicate) {
        toast.info('Generation already in progress');
      } else {
        toast.success('Visual generated successfully!');
        fetchAssets();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Generation failed. Please try again.');
    }
    setGenerating(false);
  };

  const handleSetPrimary = async (assetId) => {
    try {
      await api.post(`/visual-assets/${assetId}/set-primary`);
      toast.success('Set as primary image');
      fetchAssets();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Delete this visual?')) return;
    try {
      await api.delete(`/visual-assets/${assetId}`);
      toast.success('Deleted');
      fetchAssets();
    } catch { toast.error('Failed'); }
  };

  if (!product) return <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center"><p>Loading...</p></div>;

  return (
    <div className="min-h-screen bg-[#F0F2F5] pb-20">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        {/* Header */}
        <div className="bg-[#128C7E] text-white px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)}><ChevronLeft size={24} /></button>
          <div className="flex-1">
            <h1 className="text-base font-bold">Visual Commerce</h1>
            <p className="text-[10px] opacity-80">{product.ai_title || product.name || 'Product'}</p>
          </div>
        </div>

        <div className="p-4 space-y-4">
          {/* Product Preview */}
          <div className="flex gap-3 items-center">
            <div className="w-14 h-14 rounded-xl bg-gray-100 overflow-hidden flex-shrink-0">
              {product.images?.[0] && <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${product.images[0].path}`} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-slate-800 truncate">{product.ai_title || 'Untitled'}</p>
              {product.ai_analysis?.product_type && <p className="text-xs text-gray-500">{product.ai_analysis.product_type}</p>}
            </div>
          </div>

          {/* Style Selector */}
          <div>
            <h3 className="text-sm font-bold text-slate-700 mb-2">Photography Style</h3>
            <div className="space-y-2">
              {STYLES.map(s => (
                <button key={s.id} onClick={() => { setSelectedStyle(s.id); setShowConfig(s.id === 'model_photoshoot'); }}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${selectedStyle === s.id ? 'border-[#128C7E] bg-green-50' : 'border-gray-100 bg-white'}`}
                  data-testid={`style-${s.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}><s.icon size={18} /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800">{s.label}</p>
                    <p className="text-[10px] text-gray-500">{s.desc} — Best for: {s.best}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Model Photoshoot Config */}
          {showConfig && selectedStyle === 'model_photoshoot' && (
            <div className="space-y-3 bg-pink-50 p-4 rounded-xl border border-pink-200">
              <h4 className="text-sm font-bold text-pink-800">Model Configuration</h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-gray-500">Gender</label>
                  <div className="flex gap-1 mt-1">
                    {['Woman', 'Man'].map(g => (
                      <button key={g} onClick={() => setModelConfig(p => ({...p, gender: g}))}
                        className={`flex-1 py-1.5 rounded-lg text-xs font-medium ${modelConfig.gender === g ? 'bg-pink-600 text-white' : 'bg-white text-gray-600'}`}>{g}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-gray-500">Composition</label>
                  <div className="flex gap-1 mt-1">
                    {['Full Body', 'Medium Shot'].map(c => (
                      <button key={c} onClick={() => setModelConfig(p => ({...p, composition: c}))}
                        className={`flex-1 py-1.5 rounded-lg text-[10px] font-medium ${modelConfig.composition === c ? 'bg-pink-600 text-white' : 'bg-white text-gray-600'}`}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Pose</label>
                <div className="flex flex-wrap gap-1">
                  {POSES.map(p => (
                    <button key={p} onClick={() => setModelConfig(prev => ({...prev, pose: p}))}
                      className={`px-2 py-1 rounded-full text-[10px] font-medium ${modelConfig.pose === p ? 'bg-pink-600 text-white' : 'bg-white text-gray-600 border'}`}>{p}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Environment</label>
                <div className="flex flex-wrap gap-1">
                  {ENVIRONMENTS.map(e => (
                    <button key={e} onClick={() => setModelConfig(prev => ({...prev, environment: e}))}
                      className={`px-2 py-1 rounded-full text-[10px] font-medium ${modelConfig.environment === e ? 'bg-pink-600 text-white' : 'bg-white text-gray-600 border'}`}>{e}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] text-gray-500 mb-1 block">Lighting</label>
                <div className="flex flex-wrap gap-1">
                  {LIGHTINGS.map(l => (
                    <button key={l} onClick={() => setModelConfig(prev => ({...prev, lighting: l}))}
                      className={`px-2 py-1 rounded-full text-[10px] font-medium ${modelConfig.lighting === l ? 'bg-pink-600 text-white' : 'bg-white text-gray-600 border'}`}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Additional Instructions (Optional)</label>
            <input value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Make it festive and premium"
              maxLength={200} className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#128C7E]" />
          </div>

          {/* Disclaimer */}
          <div className="flex gap-2 bg-amber-50 rounded-lg p-3 border border-amber-200">
            <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-[10px] text-amber-700">AI-generated visuals should be reviewed for product accuracy before publishing.</p>
          </div>

          {/* Generate Button */}
          <button onClick={handleGenerate} disabled={!selectedStyle || generating}
            className="w-full h-14 bg-gradient-to-r from-[#128C7E] to-[#25D366] text-white rounded-xl text-base font-bold flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-50 shadow-lg"
            data-testid="generate-visual-btn"
          >
            {generating ? <><RefreshCw size={18} className="animate-spin" /> Creating Visual...</> : <><Sparkles size={18} /> Generate Visual</>}
          </button>

          {/* Asset Gallery */}
          {assets.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-slate-700 mb-2">Generated Visuals ({assets.length})</h3>
              <div className="grid grid-cols-2 gap-2">
                {assets.map(a => (
                  <div key={a.id} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                    {a.storage_key && (
                      <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.storage_key}`} alt={a.style} className="w-full aspect-square object-cover" />
                    )}
                    <div className="p-2">
                      <p className="text-[10px] font-semibold text-slate-700 capitalize">{(a.style || a.asset_type || '').replace('_', ' ')}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {a.is_primary && <span className="text-[8px] bg-amber-100 text-amber-700 px-1 rounded font-medium">Primary</span>}
                        <span className="text-[8px] bg-purple-100 text-purple-700 px-1 rounded font-medium">AI</span>
                      </div>
                    </div>
                    <div className="absolute top-1 right-1 flex gap-1">
                      <button onClick={() => handleSetPrimary(a.id)} className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center" title="Set Primary"><Star size={12} className="text-amber-500" /></button>
                      <button onClick={() => handleDelete(a.id)} className="w-6 h-6 bg-white/90 rounded-full flex items-center justify-center" title="Delete"><Trash2 size={12} className="text-red-500" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};
